// The reminders list: what a row shows, how the open reminders are ordered (by due date, or the
// user's own order kept in `sortOrder`), how they are grouped, and the ranks a drag or a keyboard
// move assigns. Pure functions; the page (pages/Notes.tsx) renders them.
import type { DocumentSummary, NotesOrder } from '../../../shared/types';
import { localDayKey } from './format';

type Note = Pick<DocumentSummary, 'id' | 'title' | 'preview' | 'dueAt' | 'createdAt' | 'sortOrder'>;

const DEFAULT_TITLE = /^Opomnik( \d{1,2}\. \d{1,2}\. \d{4})?$/;

/**
 * The one line a reminder is listed by: its automatic title (the first sentence without the due
 * phrase, see the server's noteTitle) – or the text itself while the server has not derived one
 * yet (a reminder just typed here still carries the default "Opomnik 3. 11. 2026").
 */
export function noteLabel(n: Pick<DocumentSummary, 'title' | 'preview'>): string {
  const title = n.title.trim();
  const text = n.preview.trim();
  if (!title || (DEFAULT_TITLE.test(title) && text)) return text || title || 'Opomnik';
  return title;
}

/** Open notes by due date: overdue first, then by due date, then those without a due date (newest first). */
export function sortOpenNotes<T extends Pick<DocumentSummary, 'dueAt' | 'createdAt'>>(list: readonly T[], now: Date): T[] {
  const nowMs = now.getTime();
  const due = (n: T) => (n.dueAt ? Date.parse(n.dueAt) : Number.NaN);
  return [...list].sort((a, b) => {
    const da = due(a);
    const db = due(b);
    const ao = da < nowMs;
    const bo = db < nowMs;
    if (ao !== bo) return ao ? -1 : 1;
    const ha = !Number.isNaN(da);
    const hb = !Number.isNaN(db);
    if (ha && hb && da !== db) return da - db;
    if (ha !== hb) return ha ? -1 : 1;
    return b.createdAt.localeCompare(a.createdAt);
  });
}

/** The user's own order: ranked notes by rank, then the unranked ones by due date (like the server). */
export function sortManually<T extends Pick<DocumentSummary, 'dueAt' | 'createdAt' | 'sortOrder'>>(list: readonly T[], now: Date): T[] {
  const ranked = list.filter((n) => typeof n.sortOrder === 'number').sort((a, b) => (a.sortOrder as number) - (b.sortOrder as number));
  const rest = sortOpenNotes(
    list.filter((n) => typeof n.sortOrder !== 'number'),
    now,
  );
  return [...ranked, ...rest];
}

export function sortOpen<T extends Pick<DocumentSummary, 'dueAt' | 'createdAt' | 'sortOrder'>>(list: readonly T[], order: NotesOrder, now: Date): T[] {
  return order === 'manual' ? sortManually(list, now) : sortOpenNotes(list, now);
}

export type DueGroup = 'overdue' | 'today' | 'tomorrow' | 'later' | 'none';

export const DUE_GROUP_LABEL: Record<DueGroup, string> = {
  overdue: 'Zamujeni',
  today: 'Danes',
  tomorrow: 'Jutri',
  later: 'Pozneje',
  none: 'Brez roka',
};

export function dueGroup(dueAt: string | null | undefined, now: Date): DueGroup {
  if (!dueAt) return 'none';
  const t = Date.parse(dueAt);
  if (Number.isNaN(t)) return 'none';
  if (t < now.getTime()) return 'overdue';
  const day = localDayKey(new Date(t));
  if (day === localDayKey(now)) return 'today';
  if (day === localDayKey(new Date(now.getTime() + 86_400_000))) return 'tomorrow';
  return 'later';
}

/** The open notes in their due order, grouped (empty groups left out, in this order). */
export function groupByDue<T extends Pick<DocumentSummary, 'dueAt' | 'createdAt'>>(list: readonly T[], now: Date): Array<{ group: DueGroup; notes: T[] }> {
  const sorted = sortOpenNotes(list, now);
  const groups: DueGroup[] = ['overdue', 'today', 'tomorrow', 'later', 'none'];
  return groups.map((group) => ({ group, notes: sorted.filter((n) => dueGroup(n.dueAt, now) === group) })).filter((g) => g.notes.length > 0);
}

export interface RankChange {
  id: string;
  sortOrder: number;
}

/**
 * The ranks to store after the note at `from` is moved to `to` in `list` (the open notes in their
 * current order): the moved note gets a rank between its new neighbours when both have one (or
 * sit at an end), otherwise – first move, or a neighbour never moved – every note of the list is
 * ranked 1, 2, 3, … in the new order, and only the changed ones are returned. Empty when nothing moves.
 */
export function rankChanges(list: readonly Note[], from: number, to: number): RankChange[] {
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return [];
  const order = [...list];
  const [moved] = order.splice(from, 1);
  order.splice(to, 0, moved!);
  const prev = order[to - 1];
  const next = order[to + 1];
  const prevRank = prev === undefined ? Number.NEGATIVE_INFINITY : (prev.sortOrder ?? null);
  const nextRank = next === undefined ? Number.POSITIVE_INFINITY : (next.sortOrder ?? null);
  if (prevRank !== null && nextRank !== null && prevRank < nextRank) {
    const rank =
      prevRank === Number.NEGATIVE_INFINITY && nextRank === Number.POSITIVE_INFINITY
        ? 0
        : prevRank === Number.NEGATIVE_INFINITY
          ? nextRank - 1
          : nextRank === Number.POSITIVE_INFINITY
            ? prevRank + 1
            : (prevRank + nextRank) / 2;
    // Ranks too close together (after many moves between the same two) get renumbered instead.
    if (rank !== prevRank && rank !== nextRank && Number.isFinite(rank)) return [{ id: moved!.id, sortOrder: rank }];
  }
  return order.map((n, i) => ({ id: n.id, sortOrder: i + 1 })).filter((c) => list.find((n) => n.id === c.id)?.sortOrder !== c.sortOrder);
}

/**
 * Where a dragged row goes among the other rows: the index of the first row whose vertical
 * middle lies below the pointer (`middles` of the other rows, in their current order), or the
 * place after the last one when the pointer is below every middle.
 */
export function dropIndex(middles: readonly number[], pointerY: number): number {
  const i = middles.findIndex((m) => pointerY < m);
  return i < 0 ? middles.length : i;
}
